import { FC, useState } from 'react';
import {
    Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tab, Tooltip, Typography,
} from '@mui/material';
import { useUserInquiries, useUserPushHistories, useUserReports } from '../../../../hooks/useUser';
import { fmtDate } from '../../userShowUtils';
import { PaginatedSection } from '../../userShowComponents';

/** ④-7. 운영/CS — 푸시 발송 · 신고 · 문의 */
const OpsTab: FC<{ userId: number }> = ({ userId }) => {
    const [subTab, setSubTab] = useState(0);
    const [pushPage, setPushPage] = useState(1);
    const [reportPage, setReportPage] = useState(1);
    const [inquiryPage, setInquiryPage] = useState(1);

    const { data: pushData, isLoading: pushLoading } = useUserPushHistories(userId, pushPage - 1);
    const { data: reportData, isLoading: reportLoading } = useUserReports(userId, reportPage - 1);
    const { data: inquiryData, isLoading: inquiryLoading } = useUserInquiries(userId, inquiryPage - 1);

    return (
        <Box>
            <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tab label="푸시 발송" />
                <Tab label="신고" />
                <Tab label="문의" />
            </Tabs>

            {subTab === 0 && (
                <PaginatedSection
                    loading={pushLoading} empty={(pushData?.content ?? []).length === 0} emptyText="발송된 푸시가 없습니다."
                    page={pushPage} totalPages={pushData?.totalPages ?? 0} onPageChange={setPushPage}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>발송일</TableCell>
                                <TableCell>유형</TableCell>
                                <TableCell>제목</TableCell>
                                <TableCell>본문</TableCell>
                                <TableCell align="right">결과</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(pushData?.content ?? []).map((h) => (
                                <TableRow key={h.id}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(h.sentAt)}</TableCell>
                                    <TableCell><Chip label={h.targetType} size="small" variant="outlined" /></TableCell>
                                    <TableCell><Typography variant="body2" fontWeight={600}>{h.title}</Typography></TableCell>
                                    <TableCell>
                                        <Tooltip title={h.body}>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>{h.body}</Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="caption" color={h.successCount > 0 ? 'success.main' : 'error.main'}>
                                            성공 {h.successCount} / 실패 {h.failureCount}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </PaginatedSection>
            )}

            {subTab === 1 && (
                <PaginatedSection
                    loading={reportLoading} empty={(reportData?.content ?? []).length === 0} emptyText="신고 이력이 없습니다."
                    page={reportPage} totalPages={reportData?.totalPages ?? 0} onPageChange={setReportPage}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>일시</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>유형</TableCell>
                                <TableCell>단어</TableCell>
                                <TableCell>신고 내용</TableCell>
                                <TableCell>관리자 메모</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(reportData?.content ?? []).map((r) => (
                                <TableRow key={r.id}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.createdAt)}</TableCell>
                                    <TableCell><Chip size="small" label={r.statusDescription} /></TableCell>
                                    <TableCell><Chip size="small" label={r.reportTypeDescription} variant="outlined" /></TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{r.wordText ?? `#${r.domainWordId}`}</Typography>
                                        <Typography variant="caption" color="text.secondary">{r.meaningText ?? ''}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={r.reportText}>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>{r.reportText}</Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                                            {r.adminNote ?? '-'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </PaginatedSection>
            )}

            {subTab === 2 && (
                <PaginatedSection
                    loading={inquiryLoading} empty={(inquiryData?.content ?? []).length === 0} emptyText="문의 이력이 없습니다."
                    page={inquiryPage} totalPages={inquiryData?.totalPages ?? 0} onPageChange={setInquiryPage}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>일시</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>카테고리</TableCell>
                                <TableCell>제목</TableCell>
                                <TableCell>답변일</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(inquiryData?.content ?? []).map((q) => (
                                <TableRow key={q.id}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(q.createdAt)}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={q.statusDescription} color={q.hasAnswer ? 'success' : 'warning'} />
                                    </TableCell>
                                    <TableCell><Chip size="small" label={q.categoryDescription} variant="outlined" /></TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{q.title}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{q.answeredAt ? fmtDate(q.answeredAt) : '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </PaginatedSection>
            )}
        </Box>
    );
};

export default OpsTab;
